import { ChevronLeft } from "lucide-react";

const TermsPage = ({ navigateTo }) => {
  return (
    <div className="min-h-screen py-16 px-4" style={{ background: "var(--zen-bg)" }}>
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigateTo("landing")}
          className="flex items-center gap-1 text-sm mb-8 hover:opacity-70 transition-opacity"
          style={{ color: "var(--zen-sub)" }}
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="font-heading text-3xl sm:text-4xl mb-2" style={{ color: "var(--zen-text)" }}>
          Terms of Service
        </h1>
        <p className="text-sm mb-10" style={{ color: "var(--zen-dim)" }}>
          Last updated: March 19, 2026
        </p>

        <div className="space-y-8 text-sm leading-relaxed" style={{ color: "var(--zen-sub)" }}>
          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>1. Agreement to Terms</h2>
            <p>
              By accessing or using JapanUnfolded (available at japanunfolded.ai), you agree to be bound by these
              Terms of Service. If you do not agree to these terms, please do not use the service.
              JapanUnfolded is operated by JapanUnfolded ("we", "us", "our").
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>2. Description of Service</h2>
            <p>
              JapanUnfolded is an AI-powered Japan travel planning tool. We help you organize cities, dates,
              preferences, and places into a day-by-day itinerary. The service uses artificial intelligence
              (Anthropic Claude) to generate personalized travel itineraries, suggest places, and provide travel
              recommendations. Generated itineraries are suggestions only and should be verified independently.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>3. User Accounts</h2>
            <p>
              You may use JapanUnfolded without creating an account. To save trips and access purchased features,
              you authenticate via a magic link sent to your email address. You are responsible for maintaining the
              security of your email account. We do not use passwords.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>4. Purchases and Payment</h2>
            <p className="mb-3">
              JapanUnfolded offers one-time purchase trip packs that grant a set number
              of AI itinerary generations. All prices are listed in US Dollars (USD). Payments are processed
              securely by Stripe, Inc. We do not store your credit card information.
            </p>
            <p className="mb-3">
              Current packages:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Discover — $4.99 USD (3 generations)</li>
              <li>Journey — $14.99 USD (12 generations)</li>
              <li>Unfolded — $29.99 USD (40 generations)</li>
            </ul>
            <p className="mt-3">
              Prices are subject to change. Purchased credits do not expire. Each itinerary generation
              consumes one credit.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>5. Refund Policy</h2>
            <p>
              Due to the digital nature of our service, all sales are final once credits have been used.
              Unused credits may be eligible for a refund within 14 days of purchase. Please see our{" "}
              <button onClick={() => navigateTo("refund-policy")} className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                Refund Policy
              </button>{" "}
              for full details.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Use the service for any unlawful purpose</li>
              <li>Attempt to reverse-engineer, scrape, or extract data from the service in bulk</li>
              <li>Interfere with the operation or security of the service</li>
              <li>Resell or redistribute generated itineraries commercially without permission</li>
              <li>Use automated scripts or bots to access the service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>7. Intellectual Property</h2>
            <p>
              The JapanUnfolded brand, design, code, and content are owned by us. Generated itineraries
              are provided for your personal, non-commercial use. You may share your itineraries with
              travel companions. Place descriptions and recommendations may incorporate publicly available
              information.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>8. Disclaimer of Warranties</h2>
            <p>
              JapanUnfolded is provided "as is" without warranties of any kind, express or implied.
              We do not guarantee the accuracy, completeness, or reliability of AI-generated itineraries.
              Travel conditions, opening hours, prices, and availability of places may change. Always
              verify important details before traveling.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, JapanUnfolded shall not be liable for any indirect,
              incidental, special, or consequential damages arising from your use of the service. Our total
              liability shall not exceed the amount you paid for the service in the preceding 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>10. Changes to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. Changes will be posted on this page
              with an updated "Last updated" date. Continued use of the service after changes constitutes
              acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>11. Contact</h2>
            <p>
              If you have any questions about these Terms, please contact us at{" "}
              <a href="mailto:support@japanunfolded.ai" className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                support@japanunfolded.ai
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
